import {
  IconDeviceMobileCheck,
  IconKey,
  IconListCheck,
  IconLock,
  IconMessageOff,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AddPasskeyDialog } from "@/components/settings/add-passkey-dialog";
import { AuthenticatorSetupDialog } from "@/components/settings/authenticator-setup-dialog";
import { BackupCodesDialog } from "@/components/settings/backup-codes-dialog";
import { DisableAuthenticatorDialog } from "@/components/settings/disable-authenticator-dialog";
import {
  PasskeyList,
  type PasskeyRecord,
} from "@/components/settings/passkey-list";
import {
  DeviceSessionList,
  type SessionRecord,
} from "@/components/settings/device-session-list";
import { SignInMethodRow } from "@/components/settings/sign-in-method-row";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  authClient,
  passkey,
  passkeyExtend,
  supportsPasskeys,
  twoFactor,
  twoFactorExtend,
  useListPasskeys,
  useSession,
} from "@/lib/auth-client";

export const Route = createFileRoute("/dashboard/settings/security")({
  component: SecurityPage,
  staticData: {
    title: "Security",
  },
});

function PasskeyHero({
  onAdd,
  canAdd,
}: {
  onAdd: () => void;
  canAdd: boolean;
}) {
  return (
    <div className="border-border bg-muted/40 mb-8 rounded-lg border p-6">
      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="bg-background text-foreground flex size-10 shrink-0 items-center justify-center rounded-md"
        >
          <IconKey className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">
            Set up a passkey — once, and you are done
          </h2>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            A passkey lives in your device&rsquo;s fingerprint or face sensor
            and never leaves it. There is no code to type, nothing to copy from
            your phone, and nothing an attacker can phish out of you. It
            replaces your password and your authenticator code in a single step.
          </p>
          <div className="mt-4">
            <Button type="button" onClick={onAdd} disabled={!canAdd}>
              <IconKey className="size-4" aria-hidden="true" />
              Add a passkey
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SecurityPage() {
  const { data, isPending, refetch } = useListPasskeys();
  const { data: session, refetch: refetchSession } = useSession();
  const [addOpen, setAddOpen] = useState(false);
  const [canUsePasskeys] = useState(supportsPasskeys);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [revealedCodes, setRevealedCodes] = useState<string[] | null>(null);
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);
  const [sessions, setSessions] = useState<SessionRecord[] | null>(null);
  const [lastUsed, setLastUsed] = useState<Record<string, string | null>>({});

  // Last-used times come from a separate endpoint, not from useListPasskeys:
  // Better Auth rebuilds each passkey row from the plugin's declared schema,
  // and this column is not part of it. See services/auth/src/plugins/passkey.
  const loadLastUsed = useCallback(async () => {
    const result = await passkeyExtend.lastUsed();
    setLastUsed(result?.data ?? {});
  }, []);

  useEffect(() => {
    void loadLastUsed();
  }, [loadLastUsed]);

  const loadSessions = useCallback(async () => {
    const result = await authClient.listSessions();
    // null rather than [] on failure: the list renders an empty array as "no
    // sessions", which is never true for someone reading this page.
    setSessions((result?.data as SessionRecord[] | undefined) ?? null);
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const twoFactorEnabled = Boolean(
    (session?.user as { twoFactorEnabled?: boolean } | undefined)
      ?.twoFactorEnabled,
  );

  const loadCodeStatus = useCallback(async () => {
    if (!twoFactorEnabled) {
      setRemainingCodes(null);
      return;
    }
    const result = await twoFactorExtend.backupCodeStatus();
    setRemainingCodes(result?.data?.remaining ?? null);
  }, [twoFactorEnabled]);

  useEffect(() => {
    void loadCodeStatus();
  }, [loadCodeStatus]);

  const passkeys: PasskeyRecord[] = Array.isArray(data)
    ? (data as PasskeyRecord[])
    : [];
  const count = passkeys.length;
  const hasPasskeys = count > 0;

  async function handleAdd(name: string): Promise<string | null> {
    // Deliberately not passing `name` here. The plugin forwards it as the
    // WebAuthn *userName*, which is the account identifier the OS prompt and
    // password managers display — sending "Mac" would leave the user with a
    // credential labelled after their laptop instead of their email address,
    // with nothing to say which account it belongs to. Letting it default to
    // the email keeps that right, and the device label is applied to our own
    // row immediately afterwards.
    //
    // `platform` pins enrolment to the authenticator built into this device —
    // Touch ID, Face ID, Windows Hello. Left unset, the OS also offers "use a
    // phone or tablet" and leads with a QR code, which is a confusing detour
    // when the laptop in front of you can be the passkey. Enrol on each device
    // you actually sign in from.
    const result = await passkey.addPasskey({
      authenticatorAttachment: "platform",
    });
    // Dismissing the OS sheet is a normal thing to do, not a failure worth
    // shouting about — close quietly and let them try again.
    if (result?.error) {
      const code =
        "code" in result.error && typeof result.error.code === "string"
          ? result.error.code
          : undefined;
      if (code === "ERROR_CEREMONY_ABORTED" || code === "NotAllowedError") {
        return null;
      }
      if (code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") {
        return "This device already has a passkey for iPlant.";
      }
      return result.error.message || "That didn't work. Try again.";
    }

    const created = result?.data as { id?: string } | undefined;
    if (created?.id) {
      // Best-effort: the passkey is already registered and usable, so a failed
      // rename is not worth failing enrolment over — it just shows unnamed.
      await passkey.updatePasskey({ id: created.id, name }).catch(() => {});
    }

    await refetch();
    return null;
  }

  return (
    <div className="max-w-4xl">
      {!isPending && !hasPasskeys && (
        <PasskeyHero onAdd={() => setAddOpen(true)} canAdd={canUsePasskeys} />
      )}

      <section>
        <h2 className="text-base font-semibold">Sign-in methods</h2>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm leading-relaxed">
          Passkeys are the strongest option and the one we recommend: the key
          never leaves your device, and there is nothing to phish. Everything
          below it is a fallback.
        </p>

        <Card className="mt-4 gap-0 overflow-hidden p-0">
          <SignInMethodRow
            icon={IconKey}
            title="Passkeys"
            status={
              hasPasskeys
                ? {
                    label: `Active · ${count} ${count === 1 ? "device" : "devices"}`,
                    tone: "good",
                  }
                : { label: "Not set up", tone: "attention" }
            }
            description={
              hasPasskeys
                ? "Touch ID, Face ID or a hardware key. Covers both your password and your second factor in one step."
                : "Nothing registered yet. If your device unlocks with a fingerprint or your face, it can become a passkey in about thirty seconds."
            }
            actions={
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAddOpen(true)}
                disabled={!canUsePasskeys}
              >
                <IconKey className="size-4" aria-hidden="true" />
                Add passkey
              </Button>
            }
          >
            <PasskeyList
              passkeys={passkeys}
              lastUsed={lastUsed}
              isLoading={isPending}
              onRemove={async (id) => {
                await passkey.deletePasskey({ id });
                await refetch();
                await loadLastUsed();
              }}
              onRename={async (id, name) => {
                await passkey.updatePasskey({ id, name });
                await refetch();
              }}
            />
          </SignInMethodRow>

          <SignInMethodRow
            icon={IconDeviceMobileCheck}
            title="Authenticator app"
            status={
              twoFactorEnabled
                ? { label: "On", tone: "good" }
                : { label: "Off", tone: "attention" }
            }
            description={
              twoFactorEnabled
                ? "Six-digit codes from your authenticator app. Used when you sign in from a device without a passkey."
                : "Not configured. Scan a QR code with Google Authenticator, Microsoft Authenticator or 1Password to turn it on."
            }
            actions={
              twoFactorEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDisableOpen(true)}
                >
                  Turn off
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSetupOpen(true)}
                >
                  Set up
                </Button>
              )
            }
          />

          <SignInMethodRow
            icon={IconListCheck}
            title="Recovery codes"
            status={
              !twoFactorEnabled
                ? { label: "Not generated", tone: "muted" }
                : remainingCodes === null
                  ? { label: "Checking…", tone: "muted" }
                  : {
                      label: `${remainingCodes} of 10 unused`,
                      tone: remainingCodes <= 3 ? "attention" : "good",
                    }
            }
            description={
              twoFactorEnabled
                ? "Single-use codes for when you have neither your passkey nor your phone. Store them where you keep your site access cards."
                : "Generated once a second factor is on. Print them and keep them with your site access cards."
            }
            actions={
              twoFactorEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const result = await twoFactor.generateBackupCodes({});
                    const codes = result?.data?.backupCodes;
                    if (Array.isArray(codes)) setRevealedCodes(codes);
                    await loadCodeStatus();
                  }}
                >
                  Regenerate
                </Button>
              ) : undefined
            }
          />

          <SignInMethodRow
            icon={IconLock}
            title="Password"
            status={
              hasPasskeys
                ? { label: "Fallback only", tone: "muted" }
                : { label: "Your only way in", tone: "attention" }
            }
            description={
              hasPasskeys
                ? "Still works on devices where you have not registered a passkey."
                : "Until you add a passkey, this password is the only thing in front of your account."
            }
          />

          {/* Static by design. Saying plainly that SMS is not accepted, and
              why, is more useful than silently omitting it — people ask. */}
          <SignInMethodRow
            icon={IconMessageOff}
            title="SMS codes"
            status={{ label: "Not offered", tone: "muted" }}
            description="Text-message codes can be intercepted by SIM swap and are not accepted on this workspace."
          />
        </Card>

        {!canUsePasskeys && (
          <p className="text-muted-foreground mt-3 text-xs">
            This browser doesn&rsquo;t support passkeys, so they can&rsquo;t be
            added here. Try Safari or Chrome on a device with a fingerprint or
            face sensor.
          </p>
        )}
      </section>

      <section>
        <h2 className="text-base font-semibold">Devices &amp; sessions</h2>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm leading-relaxed">
          Everywhere you are currently signed in. If you no longer have one of
          these devices — or you do not recognise it — end its session here.
        </p>

        <Card className="mt-4 gap-0 overflow-hidden p-0">
          <DeviceSessionList
            sessions={sessions ?? []}
            currentToken={session?.session?.token ?? null}
            isLoading={sessions === null && !session}
            onRevoke={async (tokens) => {
              // One row can hold several sessions on the same device. End them
              // all before reloading, so the row goes in one step rather than
              // shrinking once per request.
              await Promise.all(
                tokens.map((token) => authClient.revokeSession({ token })),
              );
              await loadSessions();
            }}
            onRevokeOthers={async () => {
              await authClient.revokeOtherSessions();
              await loadSessions();
            }}
          />
        </Card>
      </section>

      <AddPasskeyDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onConfirm={handleAdd}
      />

      <AuthenticatorSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onEnable={async (password) => {
          const result = await twoFactor.enable({ password });
          if (result?.error) {
            return {
              error:
                result.error.message ||
                "That password wasn't accepted. Try again.",
            };
          }
          return {
            totpURI: result.data?.totpURI ?? "",
            backupCodes: result.data?.backupCodes ?? [],
          };
        }}
        onVerify={async (code) => {
          const result = await twoFactor.verifyTotp({ code });
          if (result?.error) {
            return "That code wasn't accepted. Wait for the next one and try again.";
          }
          return null;
        }}
        onDone={async (codes) => {
          setRevealedCodes(codes);
          await refetchSession();
          await loadCodeStatus();
        }}
      />

      <DisableAuthenticatorDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        onConfirm={async (password) => {
          const result = await twoFactor.disable({ password });
          if (result?.error) {
            return (
              result.error.message ||
              "That password wasn't accepted. Try again."
            );
          }
          await refetchSession();
          setRemainingCodes(null);
          return null;
        }}
      />

      <BackupCodesDialog
        codes={revealedCodes}
        onClose={() => setRevealedCodes(null)}
      />
    </div>
  );
}
